/**
 * Pricing Validation Service
 * Enforces safety constraints and validates pricing updates before sending to WebHotelier
 */

import { supabase } from './supabaseClient';
import {
  handleDatabaseOperation,
  type ErrorHandlingResult,
} from '../utils/errorHandling';

export interface PricingConstraints {
  minPrice: number;
  maxPrice: number;
  maxChangePercent: number; // 0-100 (e.g., 30 = 30%)
  minConfidence: number; // 0-1 (e.g., 0.7 = 70%)
  minLos?: number;
  maxLos?: number;
  currency?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  adjustedPrice?: number;
  appliedConstraints: string[];
}

export interface PriceValidationRequest {
  roomTypeCode: string;
  roomTypeId?: string;
  date: Date;
  suggestedPrice: number;
  currentPrice: number;
  confidence?: number;
  constraints: PricingConstraints;
}

export class PricingValidationService {
  /**
   * Validate a price update against constraints
   */
  validatePriceUpdate(request: PriceValidationRequest): ValidationResult {
    const {
      suggestedPrice,
      currentPrice,
      confidence = 1.0,
      constraints,
      date,
      roomTypeCode: _roomTypeCode,
      roomTypeId: _roomTypeId,
    } = request;

    const errors: string[] = [];
    const warnings: string[] = [];
    const appliedConstraints: string[] = [];
    let adjustedPrice: number | undefined;

    // 1. Validate confidence level
    if (confidence < constraints.minConfidence) {
      errors.push(
        `Confidence too low: ${(confidence * 100).toFixed(1)}% < ${(constraints.minConfidence * 100).toFixed(1)}%`
      );
      return {
        isValid: false,
        errors,
        warnings,
        appliedConstraints,
      };
    }

    // 2. Validate date is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      errors.push(
        `Cannot update past date: ${date.toISOString().split('T')[0]}`
      );
      return {
        isValid: false,
        errors,
        warnings,
        appliedConstraints,
      };
    }

    // 3. Validate price is a positive number
    if (suggestedPrice <= 0) {
      errors.push(`Price must be positive: ${suggestedPrice}`);
      return {
        isValid: false,
        errors,
        warnings,
        appliedConstraints,
      };
    }

    if (currentPrice <= 0) {
      errors.push(`Current price must be positive: ${currentPrice}`);
      return {
        isValid: false,
        errors,
        warnings,
        appliedConstraints,
      };
    }

    // 4. Calculate price change percentage
    const changePercent =
      (Math.abs(suggestedPrice - currentPrice) / currentPrice) * 100;

    if (changePercent > constraints.maxChangePercent) {
      warnings.push(
        `Large price change: ${changePercent.toFixed(1)}% exceeds limit of ${constraints.maxChangePercent}%`
      );

      // Adjust to maximum allowed change
      const maxChange = currentPrice * (constraints.maxChangePercent / 100);
      if (suggestedPrice > currentPrice) {
        adjustedPrice = currentPrice + maxChange;
      } else {
        adjustedPrice = currentPrice - maxChange;
      }
      appliedConstraints.push(
        `Max change constraint: ${changePercent.toFixed(1)}% → ${constraints.maxChangePercent}%`
      );
    }

    // 5. Apply min/max price constraints
    let finalPrice =
      adjustedPrice !== undefined ? adjustedPrice : suggestedPrice;

    if (finalPrice < constraints.minPrice) {
      warnings.push(
        `Price ${finalPrice.toFixed(2)} below minimum ${constraints.minPrice.toFixed(2)}`
      );
      adjustedPrice = constraints.minPrice;
      appliedConstraints.push(
        `Min price constraint: €${finalPrice.toFixed(2)} → €${constraints.minPrice.toFixed(2)}`
      );
      finalPrice = adjustedPrice;
    }

    if (finalPrice > constraints.maxPrice) {
      warnings.push(
        `Price ${finalPrice.toFixed(2)} above maximum ${constraints.maxPrice.toFixed(2)}`
      );
      adjustedPrice = constraints.maxPrice;
      appliedConstraints.push(
        `Max price constraint: €${finalPrice.toFixed(2)} → €${constraints.maxPrice.toFixed(2)}`
      );
      finalPrice = adjustedPrice;
    }

    // 6. Round to 2 decimal places
    if (adjustedPrice !== undefined) {
      adjustedPrice = Math.round(adjustedPrice * 100) / 100;
    }

    // 7. Check if price change is too small (less than €1)
    const absoluteChange = Math.abs(finalPrice - currentPrice);
    if (absoluteChange < 1.0 && absoluteChange > 0) {
      warnings.push(`Minimal price change: €${absoluteChange.toFixed(2)}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      adjustedPrice,
      appliedConstraints,
    };
  }

  /**
   * Validate Length of Stay restrictions
   */
  validateLosRestrictions(minLos?: number, maxLos?: number): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (minLos !== undefined) {
      if (minLos < 1 || minLos > 30) {
        errors.push(`Min LOS must be between 1 and 30: ${minLos}`);
      }
    }

    if (maxLos !== undefined) {
      if (maxLos < 1 || maxLos > 365) {
        errors.push(`Max LOS must be between 1 and 365: ${maxLos}`);
      }
    }

    if (minLos !== undefined && maxLos !== undefined && minLos > maxLos) {
      errors.push(
        `Min LOS (${minLos}) cannot be greater than Max LOS (${maxLos})`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      appliedConstraints: [],
    };
  }

  /**
   * Validate date range
   */
  validateDateRange(startDate: Date, endDate: Date): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check dates are not in the past
    if (startDate < today) {
      errors.push(
        `Start date cannot be in the past: ${startDate.toISOString().split('T')[0]}`
      );
    }

    // Check end date is after start date
    if (endDate <= startDate) {
      errors.push('End date must be after start date');
    }

    // Check date range is not too large (e.g., more than 365 days)
    const daysDiff = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysDiff > 365) {
      errors.push(`Date range too large: ${daysDiff} days (maximum: 365)`);
    }

    // Warning for very long date ranges
    if (daysDiff > 90) {
      warnings.push(
        `Large date range: ${daysDiff} days may take longer to process`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      appliedConstraints: [],
    };
  }

  /**
   * Apply constraints to a price and return adjusted value
   */
  applyConstraints(price: number, constraints: PricingConstraints): number {
    let adjustedPrice = price;

    // Apply min/max constraints
    if (adjustedPrice < constraints.minPrice) {
      adjustedPrice = constraints.minPrice;
    }

    if (adjustedPrice > constraints.maxPrice) {
      adjustedPrice = constraints.maxPrice;
    }

    // Round to 2 decimal places
    adjustedPrice = Math.round(adjustedPrice * 100) / 100;

    return adjustedPrice;
  }

  /**
   * Load room type constraints from database
   */
  async getRoomTypeConstraints(
    propertyId: string,
    roomTypeId: string
  ): Promise<ErrorHandlingResult<PricingConstraints>> {
    return handleDatabaseOperation(async () => {
      // First try to get specific constraints for this room type
      const { data: constraints, error } = await supabase
        .from('room_type_price_constraints')
        .select('*')
        .eq('property_id', propertyId)
        .eq('room_type_id', roomTypeId)
        .eq('active', true)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (constraints) {
        return {
          minPrice: constraints.min_price,
          maxPrice: constraints.max_price,
          maxChangePercent: constraints.max_price_change_percent || 30,
          minConfidence: 0.7, // Default
          minLos: constraints.min_los,
          maxLos: constraints.max_los,
          currency: constraints.currency || 'EUR',
        };
      }

      // Fallback: Use default constraints
      return {
        minPrice: 50, // Default minimum
        maxPrice: 500, // Default maximum
        maxChangePercent: 30, // 30% max change
        minConfidence: 0.7, // 70% confidence
        minLos: 1,
        maxLos: 365,
        currency: 'EUR',
      };
    }, `Load constraints for room type ${roomTypeId}`);
  }

  /**
   * Validate a batch of price updates
   */
  async validateBatch(requests: PriceValidationRequest[]): Promise<{
    valid: PriceValidationRequest[];
    invalid: Array<
      PriceValidationRequest & { validationResult: ValidationResult }
    >;
    warnings: Array<
      PriceValidationRequest & { validationResult: ValidationResult }
    >;
  }> {
    const valid: PriceValidationRequest[] = [];
    const invalid: Array<
      PriceValidationRequest & { validationResult: ValidationResult }
    > = [];
    const warnings: Array<
      PriceValidationRequest & { validationResult: ValidationResult }
    > = [];

    for (const request of requests) {
      const validationResult = this.validatePriceUpdate(request);

      if (validationResult.isValid) {
        valid.push(request);
        if (
          validationResult.warnings.length > 0 ||
          validationResult.adjustedPrice !== undefined
        ) {
          warnings.push({ ...request, validationResult });
        }
      } else {
        invalid.push({ ...request, validationResult });
      }
    }

    return { valid, invalid, warnings };
  }

  /**
   * Get validation summary statistics
   */
  getValidationSummary(validationResults: ValidationResult[]): {
    total: number;
    valid: number;
    invalid: number;
    withWarnings: number;
    constraintsApplied: number;
  } {
    return {
      total: validationResults.length,
      valid: validationResults.filter((r) => r.isValid).length,
      invalid: validationResults.filter((r) => !r.isValid).length,
      withWarnings: validationResults.filter((r) => r.warnings.length > 0)
        .length,
      constraintsApplied: validationResults.filter(
        (r) => r.appliedConstraints.length > 0
      ).length,
    };
  }
}

// Export singleton instance
let pricingValidationService: PricingValidationService | null = null;

export function getPricingValidationService(): PricingValidationService {
  if (!pricingValidationService) {
    pricingValidationService = new PricingValidationService();
  }
  return pricingValidationService;
}

export default PricingValidationService;
